import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import { campareDates } from '../Utilities/Calender';
import ChargingStrategySummaryComponent from './ChargingStrategies/ChargingStrategySummaryComponent';
import { utilService } from '../services/util.service';
import translationService from '../services/translator.service';
import { AppAccordion, AppAccordionSummary, AppAccordionDetails } from './primitives/AppAccordion';
import AppMenuMyChargePointsIcon from './shared/common/icons/AppMenuMyChargePointsIcon';
import ChargingHistoryClockIcon from './shared/common/icons/ChargingHistoryClockIcon';
import ChargingHistoryFlashIcon from './shared/common/icons/ChargingHistoryFlashIcon';
import ChargingHistoryCardIcon from './shared/common/icons/ChargingHistoryCardIcon';

const TRANSACTION_ICON_SLOT_WIDTH_PX = 18;
const TRANSACTION_ICON_SIZE_PX = 14;
const TRANSACTION_ICON_VALUE_GAP_PX = 6;

const VisualStepperComponent = ({ options, newInTop, languageReducer, dataAlreadySorted, sortDirection, ...props }) => {
    const theme = useTheme();
    const classes = useStyles(props);
    const [translatedData, setTranslatedData] = useState();
    const iconOptions = {
        size: TRANSACTION_ICON_SIZE_PX,
        activeColor: theme.palette.text.primary,
        inactiveColor: theme.palette.text.primary,
    };

    useEffect(() => {
        setTranslatedData(translationService.getTranslatedData(languageReducer.language, 'visualStepperComponent'));
    }, [languageReducer.language]);

    const sortData = (options) => {
        return sortDirection === 'ASC' ? options.sort((a, b) => campareDates(a.startDate, b.startDate)) : options.sort((a, b) => campareDates(b.startDate, a.startDate));
    };

    const getDuration = (item) => {
        // Prefer machine-readable timestamps from server SignalR payload.
        // Handle possible casing differences from JSON serialization.
        const startTs = item?.dateTime_StartCharging ?? item?.DateTime_StartCharging ?? item?.dateTimeStartCharging ?? item?.DateTimeStartCharging;

        const endTs = item?.dateTime_EndCharging ?? item?.DateTime_EndCharging ?? item?.dateTimeEndCharging ?? item?.DateTimeEndCharging;

        const durationFromTimestamps = utilService.calculateDurationHHMMSS(startTs, endTs);
        if (durationFromTimestamps) return durationFromTimestamps;

        // Fallback: legacy string fields (may be locale-formatted)
        const legacyDuration = utilService.calculateDurationHHMMSS(item?.startDate, item?.endDate);
        if (legacyDuration) return legacyDuration;

        // Last resort: keep existing behavior (some screens may still rely on DD/MM swapping)
        return utilService.calculateDateDifference(item?.startDate, item?.endDate);
    };

    const getDateTimeParts = (dateTimeValue) => {
        const rawValue = `${dateTimeValue || ''}`.trim();
        if (!rawValue) {
            return { date: '-', time: '-' };
        }

        const dateMatch = rawValue.match(/\d{2}[/-]\d{2}[/-]\d{4}/);
        const timeMatch = rawValue.match(/\d{2}:\d{2}:\d{2}/);

        if (dateMatch || timeMatch) {
            return {
                date: dateMatch ? dateMatch[0] : '-',
                time: timeMatch ? timeMatch[0] : '-',
            };
        }

        const chunks = rawValue.split(' ').filter(Boolean);
        if (chunks.length >= 2) {
            return { date: chunks[0], time: chunks[1] };
        }

        return { date: rawValue, time: '-' };
    };

    const getTransactionIconProps = () => {
        const iconColor = iconOptions.activeColor;
        return {
            size: iconOptions.size,
            primaryColor: iconColor,
            secondaryColor: iconColor,
            textColor: iconColor,
            strokeColor: iconColor,
        };
    };

    return (
        <Box className={classes.listWrapper}>
            {options &&
                sortData(options).map((item, index) => {
                    const startDateTime = getDateTimeParts(item.startDate);
                    const endDateTime = getDateTimeParts(item.endDate);

                    return (
                        <div key={index} className={classes.root}>
                            <AppAccordion>
                                <AppAccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                    <Box className={classes.summaryRow}>
                                        <Box className={classes.timesContainer}>
                                            <Box className={classes.timelineRail}>
                                                <Box className={classes.timelineDot} />
                                                <Box className={classes.timelineConnector} />
                                                <Box className={classes.timelineIconWrap}>
                                                    <AppMenuMyChargePointsIcon size={20} />
                                                </Box>
                                                <Box className={classes.timelineConnector} />
                                                <Box className={classes.timelineDot} />
                                            </Box>

                                            <Box className={classes.timelineDates}>
                                                <Typography variant='subtitle2'>{startDateTime.date}</Typography>
                                                <Typography variant='subtitle2'>{startDateTime.time}</Typography>
                                                <Typography variant='subtitle2'>{endDateTime.date}</Typography>
                                                <Typography variant='subtitle2'>{endDateTime.time}</Typography>
                                            </Box>
                                        </Box>

                                        <Box className={classes.transactionContent}>
                                            <Box className={classes.transactionMetrics}>
                                                <Box className={classes.transactionMetricRow}>
                                                    <Box className={classes.transactionMetricIcon}>
                                                        <ChargingHistoryClockIcon {...getTransactionIconProps()} />
                                                    </Box>
                                                    <Typography variant='subtitle2' className={classes.transactionMetricValue}>
                                                        {getDuration(item)}
                                                    </Typography>
                                                </Box>
                                                <Box className={classes.transactionMetricRow}>
                                                    <Box className={classes.transactionMetricIcon}>
                                                        <ChargingHistoryFlashIcon {...getTransactionIconProps()} />
                                                    </Box>
                                                    <Typography variant='subtitle2' className={classes.transactionMetricValue}>
                                                        {`${item.kwh} ${translationService.getTranslation(translatedData, 'KWH')}`}
                                                    </Typography>
                                                </Box>
                                                <Box className={classes.transactionMetricRow}>
                                                    <Box className={classes.transactionMetricIcon}>
                                                        <ChargingHistoryCardIcon {...getTransactionIconProps()} />
                                                    </Box>
                                                    <Typography variant='subtitle2' className={classes.transactionMetricValue}>
                                                        {`${item.price} ₪`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                </AppAccordionSummary>
                                <AppAccordionDetails>
                                    <Grid container spacing={1}>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'user')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.driver !== null ? item.driver : item.user}</Typography>
                                        </Grid>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'startId')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.tag_id}</Typography>
                                        </Grid>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'endId')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.finishing_tag_id}</Typography>
                                        </Grid>
                                        <Grid container item xs={4} style={{ paddingLeft: 0, paddingRight: 0 }}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'transactionNum')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.localTransactionId}</Typography>
                                        </Grid>
                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'charger')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.serialNo}</Typography>
                                        </Grid>

                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'connectorNumber')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.connectorNO}</Typography>
                                        </Grid>

                                        <Grid container item xs={4}>
                                            <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                {translationService.getTranslation(translatedData, 'chargePointNickName')}
                                            </Typography>
                                        </Grid>
                                        <Grid container item xs={8}>
                                            <Typography variant='subtitle2' className={classes.detailsValue}>{item.nickname}</Typography>
                                        </Grid>

                                        {/* {item.chargingStrategyTotalDataList?.length > 0 && */}
                                        {item.chargingStrategyTotalDataList?.length > 0 && (
                                            <Grid container item xs={8}>
                                                <Typography variant='subtitle2' className={classes.detailsLabel}>
                                                    {translationService.getTranslation(translatedData, 'chargingStrategyTitle')}
                                                </Typography>
                                                {/* <ChargingStrategySummaryComponent chargingStrategyTotalData={item.chargingStrategyTotalDataList} /> */}
                                                <ChargingStrategySummaryComponent chargingStrategyTotalData={item.chargingStrategyTotalDataList} />
                                            </Grid>
                                        )}
                                    </Grid>
                                </AppAccordionDetails>
                            </AppAccordion>
                        </div>
                    );
                })}
        </Box>
    );
};

const useStyles = makeStyles((theme) => ({
    summaryRow: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(3),
    },
    timesContainer:{
        display: 'flex',
        gap: theme.spacing(1),
    },
    timelineDates: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: theme.spacing(0.3),
        minHeight: theme.spacing(9),
        flexShrink: 0,
    },
    timelineRail: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        minWidth: theme.spacing(3),
        minHeight: theme.spacing(9),
        paddingTop: theme.spacing(0.25),
        paddingBottom: theme.spacing(0.25),
        flexShrink: 0,
        gap: theme.spacing(0.5),
        // height: '100%',
    },
    timelineConnector: {
        width: theme.spacing(0.1),
        flex: 1,
        backgroundColor: theme.palette.grey[400],
    },
    timelineDot: {
        width: theme.spacing(1),
        height: theme.spacing(1),
        borderRadius: '50%',
        backgroundColor: theme.palette.grey[400],
        flexShrink: 0,
    },
    timelineIconWrap: {
        width: theme.spacing(3),
        height: theme.spacing(3),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    transactionContent: {
        flex: 1,
        minWidth: 0,
        // height: '100%',
    },
    transactionMetrics: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    transactionMetricRow: {
        display: 'flex',
        alignItems: 'center',
        gap: TRANSACTION_ICON_VALUE_GAP_PX,
        minHeight: theme.spacing(2.5),
    },
    transactionMetricIcon: {
        flex: `0 0 ${TRANSACTION_ICON_SLOT_WIDTH_PX}px`,
        width: TRANSACTION_ICON_SLOT_WIDTH_PX,
        height: TRANSACTION_ICON_SLOT_WIDTH_PX,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.secondary,
    },
    transactionMetricValue: {
        flex: 1,
        minWidth: 0,
        lineHeight: 1.2,
    },
    detailsLabel: {
        fontWeight: theme.typography.fontWeightBold,
    },
    detailsValue: {
        fontWeight: theme.typography.fontWeightRegular,
    },
    root: {
        marginBlock: theme.spacing(0.75),
    },
    listWrapper: {
        width: '100%',
    },
}));

VisualStepperComponent.propTypes = {};

const mapStateToProps = (state) => ({
    languageReducer: state.languageReducer,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(VisualStepperComponent);
